import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { observer } from 'mobx-react-lite';
import { UserSummaryType } from '../../../../../api/types/user/user-summary.type';
import { LectureWithSessions } from '../../../../../api/types/programs/course/lectures/lecture-with-schedule-metadata';
import globalCourseStore from '../../../../../stores/global-course-store';
import globalProgramStore from '../../../../../stores/program-store';
import { updateAttendanceOperations } from '../../lecture-page-dialog/tabs/attendance/operations/update-attendance-operations';
import { startLectureAndRefreshOperation } from '../operations/start-lecture-and-refresh-operation';
import { loadLectureDetailsForAttendanceOperation } from '../operations/load-lecture-details-for-attendance-operation';
import appApi from '../../../../../api/app-api';
import { toast } from 'react-toastify';
import globalUserProfile from '../../../../../stores/global-user-profile';
import {
  AttendanceDataForm,
  AttendanceSessionPayload,
} from '../../lecture-page-dialog/tabs/attendance/componenets/attendance-data-form';
import {
  LectureAttendanceSessionWithAttendanceDB,
  LectureDetails,
} from '../../../../../api/types/lecturers/details/lecture-details';

interface LectureInfo {
  programId: number;
  courseId: number;
  lectureId: number;
}

interface StartLectureAttendanceDialogProps {
  visible: boolean;
  lecture: LectureWithSessions | null;
  onHide: () => void;
  lectureInfo?: LectureInfo;
  useProfessorMode?: boolean;
  updateAttendanceOnly?: boolean;
}

const buildStudentsFromSessions = (
  sessions: LectureAttendanceSessionWithAttendanceDB[],
): UserSummaryType[] => {
  const byId = new Map<number, UserSummaryType>();
  sessions.forEach((sessionEntry) => {
    sessionEntry.attendance.forEach((row) => {
      if (!byId.has(row.student.id)) {
        byId.set(row.student.id, row.student);
      }
    });
  });
  return Array.from(byId.values());
};

const buildSyntheticSessions = (
  sessionCount: number,
  lectureId: number,
  attendanceRequired: boolean,
): LectureAttendanceSessionWithAttendanceDB[] => {
  if (sessionCount <= 0) {
    return [];
  }

  const baseEntity = {
    createdAt: new Date(),
    createdBy: 0,
    createdByName: '',
  };

  return Array.from({ length: sessionCount }, (_, idx) => {
    const sessionNumber = idx + 1;
    return {
      session: {
        id: sessionNumber,
        lectureId: lectureId,
        sessionNumber: sessionNumber,
        sessionName: null,
        attendanceRequired: attendanceRequired,
        ...baseEntity,
      },
      attendance: [],
    };
  });
};

export const StartLectureAttendanceDialog: React.FC<StartLectureAttendanceDialogProps> =
  observer(
    ({
      visible,
      lecture,
      onHide,
      lectureInfo,
      useProfessorMode,
      updateAttendanceOnly,
    }) => {
      const [lectureDetails, setLectureDetails] =
        useState<LectureDetails | null>(null);
      const [courseStudents, setCourseStudents] = useState<UserSummaryType[]>(
        [],
      );
      const [isLoadingDetails, setIsLoadingDetails] = useState(false);
      const [isSaving, setIsSaving] = useState(false);

      useEffect(() => {
        if (!visible) {
          setLectureDetails(null);
          setCourseStudents([]);
          return;
        }

        const loadDetails = async () => {
          let programId: number | undefined;
          let courseId: number | undefined;
          let lectureId: number | undefined;

          if (useProfessorMode && lectureInfo) {
            programId = lectureInfo.programId;
            courseId = lectureInfo.courseId;
            lectureId = lectureInfo.lectureId;
          } else if (lecture) {
            programId = globalProgramStore.getCurrentId();
            courseId = lecture.item.courseId;
            lectureId = lecture.item.id;
          }

          if (!programId || !courseId || !lectureId) {
            return;
          }

          const details = await loadLectureDetailsForAttendanceOperation(
            programId,
            courseId,
            lectureId,
            setIsLoadingDetails,
          );
          setLectureDetails(details);

          const studentsFromSessions = buildStudentsFromSessions(
            details?.attendanceSessions ?? [],
          );
          if (studentsFromSessions.length === 0) {
            try {
              const courseRes = await appApi.getCourseDetails(
                programId,
                courseId,
              );
              setCourseStudents(courseRes.data.body.details.students ?? []);
            } catch (error) {
              console.error('Failed to fetch course students:', error);
              setCourseStudents([]);
            }
          }
        };

        loadDetails();
      }, [visible, useProfessorMode, lectureInfo, lecture]);

      const sessions = useMemo<
        LectureAttendanceSessionWithAttendanceDB[]
      >(() => {
        if (lectureDetails?.attendanceSessions?.length) {
          return lectureDetails.attendanceSessions;
        }

        const sessionCount =
          lecture?.item?.attendanceSessionNumber ??
          lectureDetails?.lectureInformation?.lecture
            ?.attendanceSessionNumber ??
          0;
        const lectureId =
          lecture?.item?.id ??
          lectureDetails?.lectureInformation?.lecture?.id ??
          lectureInfo?.lectureId ??
          0;
        const attendanceRequired =
          lecture?.item?.attendanceRequired ??
          lectureDetails?.lectureInformation?.lecture?.attendanceRequired ??
          true;

        return buildSyntheticSessions(
          sessionCount,
          lectureId,
          attendanceRequired,
        );
      }, [lectureDetails, lecture, lectureInfo]);

      const students = useMemo<UserSummaryType[]>(() => {
        const fromSessions = buildStudentsFromSessions(sessions);
        if (fromSessions.length > 0) {
          return fromSessions;
        }

        if (courseStudents.length > 0) {
          return courseStudents;
        }

        return globalCourseStore.getCurrentCourse()?.details?.students ?? [];
      }, [sessions, courseStudents]);

      const handleAttendanceSubmit = async (
        sessionPayload: AttendanceSessionPayload[],
      ) => {
        const upsertPayload = sessionPayload.map((entry) => ({
          attendanceSessionsId: entry.sessionId,
          attendance: entry.attendance,
        }));

        if (upsertPayload.length === 0) {
          toast.info('לא נמצאו שינויים לעדכון');
          return;
        }

        setIsSaving(true);
        try {
          if (useProfessorMode && lectureInfo) {
            if (updateAttendanceOnly) {
              await appApi.upsetLectureAttendance(
                lectureInfo.programId,
                lectureInfo.courseId,
                lectureInfo.lectureId,
                upsertPayload,
              );

              await globalUserProfile.getUserProfile();
              toast.success('הנוכחות עודכנה בהצלחה');
            } else {
              await appApi.startLecture(
                lectureInfo.programId,
                lectureInfo.courseId,
                lectureInfo.lectureId,
              );

              await appApi.upsetLectureAttendance(
                lectureInfo.programId,
                lectureInfo.courseId,
                lectureInfo.lectureId,
                upsertPayload,
              );

              await globalUserProfile.getUserProfile();
              toast.success('ההרצאה החלה והנוכחות נשמרה בהצלחה');
            }
          } else {
            if (!lecture) return;

            const programId = globalProgramStore.getCurrentId();
            const courseId = globalCourseStore.getCurrentCourse()?.metadata.id;

            if (!programId || !courseId) return;

            await startLectureAndRefreshOperation(
              programId,
              courseId,
              lecture.item.id,
            );

            await updateAttendanceOperations(programId, lecture, upsertPayload);
          }

          onHide();
        } catch (error) {
          console.error('Error starting lecture or saving attendance:', error);
          onHide();
        } finally {
          setIsSaving(false);
        }
      };

      const dialogHeader = updateAttendanceOnly
        ? 'עדכון נתוני נוכחות'
        : 'סימון נוכחות';
      const saveButtonLabel = updateAttendanceOnly
        ? 'שמור נוכחות'
        : 'התחל הרצאה ושמור נוכחות';
      const submitMode = 'full';

      return (
        <Dialog
          header={dialogHeader}
          visible={visible}
          style={{ width: '90vw', maxWidth: '1200px' }}
          onHide={onHide}
          modal
        >
          <div>
            {isLoadingDetails ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                טוען נתוני נוכחות...
              </div>
            ) : sessions.length === 0 || students.length === 0 ? (
              <div style={{ textAlign: 'right', padding: '20px' }}>
                לא נמצאו נתוני נוכחות להצגה
              </div>
            ) : (
              <AttendanceDataForm
                sessions={sessions}
                students={students}
                handleCompleteLecture={handleAttendanceSubmit}
                submitMode={submitMode}
                isSaving={isSaving}
                onCancel={onHide}
                approveLabel={saveButtonLabel}
              />
            )}
          </div>
        </Dialog>
      );
    },
  );
